import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CalendarIcon, DocumentTextIcon, CreditCardIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  getMyApplications,
  cancelApplication as cancelApplicationAPI,
} from '../services/applicationService';
import {
  requestRefund,
  cancelRefundRequest,
  Refund,
  getRefundStatusLabel,
  getRefundStatusColor,
} from '../services/refundService';
import { supabase } from '../lib/supabase';
import { Application } from '../types';

// 자녀 관련 인터페이스 및 import 제거 - 항상 본인 신청 구조로 변경

const MyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isStudent, updateProfile, logout, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'applications'>('applications');

  // 신청 내역 상태
  const [applications, setApplications] = useState<Application[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'registered' | 'waiting' | 'cancelled' | 'refund' | 'all'>('all');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // 환불 관련 상태
  const [refundMap, setRefundMap] = useState<Record<string, Refund>>({});
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundTargetApp, setRefundTargetApp] = useState<Application | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundBank, setRefundBank] = useState('');
  const [refundAccount, setRefundAccount] = useState('');
  const [refundAccountHolder, setRefundAccountHolder] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [cancellingRefundId, setCancellingRefundId] = useState<string | null>(null);

  // 회원정보 수정 상태
  const [editMode, setEditMode] = useState(false);
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // 회원 탈퇴 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState<{ hasActiveApps: boolean; paidAmount: number } | null>(null);

  // 자녀 관리 상태 제거 - 항상 본인 신청 구조

  // user가 변경될 때마다 폼 초기화
  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setAddress(user.address || '');
      setAddressDetail(user.address_detail || '');
    }
  }, [user]);

  // 신청 내역 로드 (최초 1회만, 필터 변경 시에는 클라이언트 필터링)
  useEffect(() => {
    if (user && activeTab === 'applications' && applications.length === 0) {
      loadApplications();
    }
  }, [user, activeTab]);

  // 자녀 목록 로드 제거 - 항상 본인 신청 구조

  // URL 파라미터 처리 (탭 이동)
  useEffect(() => {
    const tab = searchParams.get('tab');

    if (tab === 'profile') {
      setActiveTab('profile');
    }

    // 자녀 추가 관련 파라미터 처리 제거
  }, [searchParams, user]);

  const loadApplications = async () => {
    setLoadingApplications(true);
    try {
      // 환불 필터는 전체 조회 후 클라이언트에서 필터링
      const apiFilter = statusFilter === 'refund' ? 'all' : statusFilter;
      const response = await getMyApplications(apiFilter);
      console.log('=== MyPage: loadApplications 응답 ===');
      console.log('response:', response);

      if (response.success && response.data) {
        console.log('applications 데이터:', response.data.applications);
        if (response.data.applications.length > 0) {
          console.log('첫 번째 신청의 class_name:', response.data.applications[0].class_name);
        }
        setApplications(response.data.applications);
        // 환불 정보 로드
        loadRefundsForApplications(response.data.applications);
      } else {
        console.error('Failed to load applications:', response.error);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  // 각 신청에 대한 환불 정보 로드
  const loadRefundsForApplications = async (apps: Application[]) => {
    const newRefundMap: Record<string, Refund> = {};

    // 환불 정보 조회 대상:
    // 1. 결제 완료된 신청 (환불 신청 가능)
    // 2. 환불 완료된 신청 (payment_status='refunded')
    // 3. 취소된 신청 중 환불로 취소된 것 (status='cancelled')
    const targetApps = apps.filter(
      app => (app.status === 'registered' && (app.payment_status === 'completed' || app.payment_status === 'refunded')) ||
        (app.status === 'cancelled' && app.payment_status === 'refunded')
    );

    if (targetApps.length === 0) {
      setRefundMap({});
      return;
    }

    // Supabase에서 직접 환불 정보 조회
    const applicationIds = targetApps.map(app => app.application_id);
    const { data: refunds, error } = await supabase
      .from('refunds')
      .select('*')
      .in('application_id', applicationIds);

    if (error) {
      console.error('Error loading refunds:', error);
      setRefundMap({});
      return;
    }

    // 환불 정보 매핑
    refunds?.forEach((refund: Refund) => {
      newRefundMap[refund.application_id] = refund;
    });

    console.log('Loaded refunds:', newRefundMap); // 디버깅용
    setRefundMap(newRefundMap);
  };

  // 필터 적용된 신청 목록
  const filteredApplications = useMemo(() => {
    if (statusFilter === 'refund') {
      // 환불 필터: 환불이 있는 신청만 표시 (사용자 취소 제외)
      return applications.filter(app => {
        const refund = refundMap[app.application_id];
        if (!refund) return false;
        // cancelled 상태인데 관리자 거절이 아니면(사용자 취소) 제외
        if (refund.refund_status === 'cancelled' && !refund.rejection_reason) return false;
        return true;
      });
    }
    if (statusFilter === 'cancelled') {
      // 신청취소 필터: 환불 완료로 인한 취소는 제외
      return applications.filter(app => {
        if (app.status !== 'cancelled') return false;
        // 환불 완료(payment_status='refunded')는 환불 필터에서 봐야 함
        if (app.payment_status === 'refunded') return false;
        return true;
      });
    }
    return applications;
  }, [applications, refundMap, statusFilter]);

  // loadChildren 함수 제거 - 항상 본인 신청 구조
  // groupedApplications 제거 - 항상 본인 신청이므로 그룹핑 불필요

  // 인증 로딩 중일 때는 로딩 스피너 표시 (새로고침 시 잠깐 "로그인해주세요" 보이는 것 방지)
  if (authLoading) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user || !isStudent) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <p className="text-gray-500">로그인해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'registered': return '접수완료';
      case 'waiting': return '대기중';
      case 'cancelled': return '신청취소';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'registered': return 'bg-green-100 text-green-800';
      case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusLabel = (paymentStatus: string | null | undefined) => {
    switch (paymentStatus) {
      case 'pending': return '결제대기';
      case 'completed': return '결제완료';
      case 'expired': return '기한만료';
      case 'cancelled': return '결제취소';
      case 'refunded': return '환불완료';
      default: return null;
    }
  };

  const getPaymentStatusColor = (paymentStatus: string | null | undefined) => {
    switch (paymentStatus) {
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isPaymentDeadlineExpired = (deadline: string | null | undefined) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const handleCancelApplication = async (applicationId: string) => {
    if (!window.confirm('수강신청을 취소하시겠습니까?\n\n취소 후 다시 신청하시려면 대기 순번이 변경될 수 있습니다.')) {
      return;
    }

    setCancellingId(applicationId);

    try {
      const response = await cancelApplicationAPI({ application_id: applicationId });

      if (response.success) {
        // 승급 정보 확인
        if (response.data?.promoted) {
          alert(`✅ 수강신청이 취소되었습니다.\n\n🎉 대기자 ${response.data.promoted.student_name}님이 자동으로 등록되었습니다.`);
        } else {
          alert('✅ 수강신청이 취소되었습니다.');
        }
        // 목록 새로고침
        await loadApplications();
      } else if (response.error) {
        const { code, message } = response.error;
        switch (code) {
          case 'APPLICATION_NOT_FOUND':
            alert('신청 내역을 찾을 수 없습니다.');
            break;
          case 'ALREADY_CANCELLED':
            alert('이미 취소된 신청입니다.');
            break;
          case 'UNAUTHORIZED':
            alert('본인의 신청만 취소할 수 있습니다.');
            break;
          default:
            alert(message || '취소 처리 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.');
        }
        await loadApplications();
      }
    } catch (error) {
      console.error('Cancel application error:', error);
      alert('네트워크 오류가 발생했습니다.\n\n인터넷 연결을 확인하고 다시 시도해주세요.');
    } finally {
      setCancellingId(null);
    }
  };

  // 환불 신청 모달 열기
  const openRefundModal = (application: Application) => {
    setRefundTargetApp(application);
    setRefundReason('');
    setRefundBank('');
    setRefundAccount('');
    setRefundAccountHolder(user?.name || '');
    setRefundModalOpen(true);
  };

  // 환불 신청 제출
  const handleSubmitRefund = async () => {
    if (!refundTargetApp) return;

    if (!refundReason.trim()) {
      alert('환불 사유를 입력해주세요.');
      return;
    }

    setSubmittingRefund(true);

    try {
      const response = await requestRefund({
        application_id: refundTargetApp.application_id,
        refund_reason: refundReason,
        refund_bank: refundBank || undefined,
        refund_account: refundAccount || undefined,
        refund_account_holder: refundAccountHolder || undefined,
      });

      if (response.success && response.data) {
        // 즉시 refundMap 업데이트
        setRefundMap(prev => ({
          ...prev,
          [refundTargetApp.application_id]: response.data!
        }));
        alert('환불 신청이 완료되었습니다.\n관리자 확인 후 처리됩니다.');
        setRefundModalOpen(false);
        // 전체 새로고침
        await loadApplications();
      } else if (response.success) {
        // data가 없는 경우에도 새로고침
        alert('환불 신청이 완료되었습니다.\n관리자 확인 후 처리됩니다.');
        setRefundModalOpen(false);
        await loadApplications();
      } else {
        const { code, message } = response.error || {};
        switch (code) {
          case 'NOT_OWNER':
            alert('본인의 신청만 환불 신청할 수 있습니다.');
            break;
          case 'INVALID_STATUS':
            alert('환불 신청이 불가능한 상태입니다.\n결제완료된 신청만 환불 가능합니다.');
            break;
          case 'DUPLICATE_REFUND':
            alert('이미 환불 신청된 상태입니다.');
            break;
          default:
            alert(message || '환불 신청 중 오류가 발생했습니다.');
        }
      }
    } catch (error) {
      console.error('Refund request error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setSubmittingRefund(false);
    }
  };

  // 환불 신청 취소
  const handleCancelRefund = async (refundId: string, applicationId: string) => {
    if (!window.confirm('환불 신청을 취소하시겠습니까?')) {
      return;
    }

    setCancellingRefundId(refundId);

    try {
      const response = await cancelRefundRequest(refundId);

      if (response.success) {
        alert('환불 신청이 취소되었습니다.');
        // 환불 맵에서 제거
        setRefundMap(prev => {
          const updated = { ...prev };
          delete updated[applicationId];
          return updated;
        });
      } else {
        const { code, message } = response.error || {};
        switch (code) {
          case 'NOT_OWNER':
            alert('본인의 환불 신청만 취소할 수 있습니다.');
            break;
          case 'INVALID_STATUS':
            alert('이미 승인된 환불은 취소할 수 없습니다.\n관리자에게 문의해주세요.');
            break;
          default:
            alert(message || '환불 취소 중 오류가 발생했습니다.');
        }
      }
    } catch (error) {
      console.error('Cancel refund error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setCancellingRefundId(null);
    }
  };

  // 회원정보 저장
  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveMessage('');

    const result = await updateProfile({
      email,
      address,
      address_detail: addressDetail,
    });

    if (result.success) {
      setSaveMessage(result.message || '저장되었습니다.');
      setEditMode(false);
      // 3초 후 메시지 제거
      setTimeout(() => setSaveMessage(''), 3000);
    } else {
      setSaveMessage(result.message || '저장에 실패했습니다.');
    }

    setSaving(false);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    if (user) {
      setEmail(user.email || '');
      setAddress(user.address || '');
      setAddressDetail(user.address_detail || '');
    }
    setEditMode(false);
    setSaveMessage('');
  };

  // =====================================================
  // 자녀 관련 함수 제거 - 항상 본인 신청 구조
  // handleOpenAddChild, handleEditChild, handleSaveChild, handleDeleteChild 삭제됨
  // =====================================================

  // 회원 탈퇴 모달 열기
  const handleOpenDeleteModal = async () => {
    // active 신청 + 결제완료 금액 조회
    let paidAmount = 0;
    let hasActiveApps = false;

    if (user) {
      const { data } = await supabase
        .from('applications')
        .select('id, status, payment_status, payment_amount')
        .eq('registrant_id', user.id)
        .in('status', ['registered', 'waiting']);

      if (data && data.length > 0) {
        hasActiveApps = true;
        paidAmount = data
          .filter(a => a.payment_status === 'completed')
          .reduce((sum, a) => sum + (a.payment_amount || 0), 0);
      }
    }

    setDeleteWarning({ hasActiveApps, paidAmount });
    setDeletePassword('');
    setShowDeleteModal(true);
  };

  // 회원 탈퇴 실행
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      alert('비밀번호를 입력해주세요.');
      return;
    }

    setDeletingAccount(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert('로그인이 필요합니다.');
        return;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;

      const response = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY!,
        },
        body: JSON.stringify({ password: deletePassword }),
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        await logout();
        alert('회원 탈퇴가 완료되었습니다.');
        navigate('/');
      } else {
        const { code, message } = result.error || {};
        switch (code) {
          case 'INVALID_PASSWORD':
            alert('비밀번호가 일치하지 않습니다.');
            break;
          case 'PENDING_REFUND':
            alert('진행 중인 환불 요청이 있습니다.\n환불 완료 후 탈퇴해주세요.');
            setShowDeleteModal(false);
            break;
          case 'ADMIN_NOT_ALLOWED':
            alert('관리자 계정은 탈퇴할 수 없습니다.');
            break;
          default:
            alert(message || '탈퇴 처리 중 오류가 발생했습니다.');
            console.error('Delete account failed:', result.error);
        }
      }
    } catch (error) {
      console.error('Delete account error:', error);
      alert('네트워크 오류가 발생했습니다. (콘솔 확인)');
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 bg-gray-50 py-12 mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">마이페이지</h1>
            <p className="text-gray-600">{user.name}님의 수강신청 내역을 확인하세요.</p>
          </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('applications')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'applications'
                    ? 'border-[#48c9a6] text-[#48c9a6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                수강신청 내역
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-[#48c9a6] text-[#48c9a6]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                회원 정보
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'applications' && (
          <div>
            {/* 상태 필터 */}
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                전체
              </button>
              <button
                onClick={() => setStatusFilter('registered')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'registered'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                접수완료
              </button>
              <button
                onClick={() => setStatusFilter('waiting')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'waiting'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                대기중
              </button>
              <button
                onClick={() => setStatusFilter('cancelled')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'cancelled'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                신청취소
              </button>
              <button
                onClick={() => setStatusFilter('refund')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === 'refund'
                    ? 'bg-[#48c9a6] text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                환불
              </button>
            </div>

            <div className="space-y-6">
              {loadingApplications ? (
                <div className="bg-white rounded-lg shadow text-center py-12">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#48c9a6] border-r-transparent"></div>
                  <p className="mt-4 text-gray-500">불러오는 중...</p>
                </div>
              ) : filteredApplications.length === 0 ? (
                <div className="bg-white rounded-lg shadow text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">신청 내역이 없습니다</h3>
                  <p className="mt-2 text-gray-500">
                    {statusFilter === 'refund'
                      ? '환불 내역이 없습니다.'
                      : statusFilter !== 'all'
                      ? `${getStatusLabel(statusFilter)} 상태의 신청 내역이 없습니다.`
                      : '아직 수강신청한 프로그램이 없습니다.'}
                  </p>
                </div>
              ) : (
                // 신청 내역 목록 표시
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="space-y-6">
                      {filteredApplications.map((application) => (
                        <div key={application.application_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                          {/* 헤더 영역 */}
                          <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                  {application.program_name}
                                  {application.class_name && application.class_name !== '기본반' && (
                                    <span className="ml-2 text-sm font-normal text-gray-500">({application.class_name})</span>
                                  )}
                                </h3>
                                {application.instructor && (
                                  <p className="text-sm text-gray-500 mt-0.5">강사: {application.instructor}</p>
                                )}
                              </div>
                              <div className="flex flex-col items-end gap-1.5">
                                <div className="flex flex-wrap gap-1.5 justify-end">
                                  {/* 환불 완료는 "환불완료"로, 그 외는 기존 상태 라벨 */}
                                  {application.payment_status === 'refunded' ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700">
                                      환불완료
                                    </span>
                                  ) : (
                                    <>
                                      <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getStatusColor(application.status)}`}>
                                        {getStatusLabel(application.status)}
                                      </span>
                                      {application.payment_status && application.status === 'registered' && (
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getPaymentStatusColor(application.payment_status)}`}>
                                          {getPaymentStatusLabel(application.payment_status)}
                                        </span>
                                      )}
                                      {/* 환불 상태 칩 (cancelled는 관리자 거절인 경우만 표시) */}
                                      {refundMap[application.application_id] &&
                                       (refundMap[application.application_id].refund_status !== 'cancelled' ||
                                        refundMap[application.application_id].rejection_reason) && (
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getRefundStatusColor(refundMap[application.application_id].refund_status)}`}>
                                          {getRefundStatusLabel(refundMap[application.application_id].refund_status)}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                                {application.status === 'waiting' && application.waiting_number && (
                                  <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                                    대기순번: {application.waiting_number}번
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 상세 정보 영역 */}
                          <div className="px-5 py-4">
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                              <div className="flex justify-between py-1.5 border-b border-gray-100">
                                <span className="text-gray-500">신청일시</span>
                                <span className="text-gray-900">
                                  {new Date(application.created_at).toLocaleString('ko-KR', {
                                    year: 'numeric', month: '2-digit', day: '2-digit',
                                    hour: '2-digit', minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              {application.tuition !== undefined && application.material_fee !== undefined && (
                                <div className="flex justify-between py-1.5 border-b border-gray-100">
                                  <span className="text-gray-500">수강료</span>
                                  <span className="text-gray-900">{(application.tuition + application.material_fee).toLocaleString()}원</span>
                                </div>
                              )}
                              {application.course_start_date && application.course_end_date && (
                                <div className="flex justify-between py-1.5 border-b border-gray-100">
                                  <span className="text-gray-500">교육기간</span>
                                  <span className="text-gray-900">{new Date(application.course_start_date).toLocaleDateString('ko-KR')} ~ {new Date(application.course_end_date).toLocaleDateString('ko-KR')}</span>
                                </div>
                              )}
                              {application.course_start_time && application.course_end_time && (
                                <div className="flex justify-between py-1.5 border-b border-gray-100">
                                  <span className="text-gray-500">교육시간</span>
                                  <span className="text-gray-900">{application.course_start_time.slice(0, 5)} ~ {application.course_end_time.slice(0, 5)}</span>
                                </div>
                              )}
                              {application.payment_amount && application.payment_amount > 0 && application.status === 'registered' && (
                                <div className="flex justify-between py-1.5 border-b border-gray-100">
                                  <span className="text-gray-500">결제금액</span>
                                  <span className="font-medium text-gray-900">{application.payment_amount.toLocaleString()}원</span>
                                </div>
                              )}
                              {application.payment_deadline && application.payment_status === 'pending' && (
                                <div className={`flex justify-between py-1.5 border-b border-gray-100 ${isPaymentDeadlineExpired(application.payment_deadline) ? 'text-red-600' : ''}`}>
                                  <span className={isPaymentDeadlineExpired(application.payment_deadline) ? 'text-red-500' : 'text-gray-500'}>결제기한</span>
                                  <span className={isPaymentDeadlineExpired(application.payment_deadline) ? 'font-medium' : 'text-gray-900'}>
                                    {(() => {
                                      // 다음 정각으로 올림 (cron이 매 정각 실행되므로)
                                      const deadline = new Date(application.payment_deadline);
                                      const nextHour = new Date(deadline);
                                      nextHour.setMinutes(0, 0, 0);
                                      if (deadline.getMinutes() > 0 || deadline.getSeconds() > 0) {
                                        nextHour.setHours(nextHour.getHours() + 1);
                                      }
                                      return nextHour.toLocaleString('ko-KR', {
                                        year: 'numeric', month: '2-digit', day: '2-digit',
                                        hour: '2-digit', minute: '2-digit'
                                      });
                                    })()}
                                    {isPaymentDeadlineExpired(application.payment_deadline) && ' (만료)'}
                                  </span>
                                </div>
                              )}
                              {application.paid_at && application.payment_status === 'completed' && (
                                <div className="flex justify-between py-1.5 border-b border-gray-100">
                                  <span className="text-gray-500">결제일</span>
                                  <span className="text-gray-900">
                                    {new Date(application.paid_at).toLocaleString('ko-KR', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              )}
                              {application.cancelled_at && (
                                <div className="flex justify-between py-1.5 border-b border-gray-100">
                                  <span className="text-gray-500">취소일</span>
                                  <span className="text-gray-900">
                                    {new Date(application.cancelled_at).toLocaleString('ko-KR', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* 무통장입금 안내 (결제대기 상태일 때만) */}
                          {application.status === 'registered' && application.payment_status === 'pending' && application.payment_amount && application.payment_amount > 0 && (
                            <div className="mx-5 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                              <h4 className="text-sm font-semibold text-amber-800 mb-3">무통장입금 안내</h4>
                              <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-amber-700">입금계좌</span>
                                  <span className="font-medium text-gray-900">농협 301-0375-1419-51</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-amber-700">예금주</span>
                                  <span className="font-medium text-gray-900">㈜한원글로벌 동두천지점</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-amber-700">입금금액</span>
                                  <span className="font-medium text-gray-900">{application.payment_amount.toLocaleString()}원</span>
                                </div>
                                {application.payment_deadline && (
                                  <div className="flex justify-between">
                                    <span className={isPaymentDeadlineExpired(application.payment_deadline) ? 'text-red-600' : 'text-amber-700'}>입금기한</span>
                                    <span className={isPaymentDeadlineExpired(application.payment_deadline) ? 'font-medium text-red-600' : 'font-medium text-gray-900'}>
                                      {(() => {
                                        // 다음 정각으로 올림 (cron이 매 정각 실행되므로)
                                        const deadline = new Date(application.payment_deadline);
                                        const nextHour = new Date(deadline);
                                        nextHour.setMinutes(0, 0, 0);
                                        if (deadline.getMinutes() > 0 || deadline.getSeconds() > 0) {
                                          nextHour.setHours(nextHour.getHours() + 1);
                                        }
                                        return nextHour.toLocaleString('ko-KR', {
                                          year: 'numeric',
                                          month: '2-digit',
                                          day: '2-digit',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        });
                                      })()}
                                      {isPaymentDeadlineExpired(application.payment_deadline) && ' (만료)'}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 pt-3 border-t border-amber-200 text-sm text-amber-800">
                                <p>* 입금자명은 실수강자(<span className="font-medium text-gray-900">{application.student_name}</span>)이름과 회원가입에 사용한 전화번호 뒷 4자리(<span className="font-medium text-gray-900">{(user?.user_type === 'minor' && user?.guardian_phone ? user.guardian_phone : user?.phone)?.slice(-4) || '0000'}</span>)를 넣어주세요. 예) <span className="font-medium text-gray-900">{application.student_name}{(user?.user_type === 'minor' && user?.guardian_phone ? user.guardian_phone : user?.phone)?.slice(-4) || '0000'}</span></p>
                                <p className="mt-1">* 입금 확인 후 결제완료로 변경됩니다.</p>
                              </div>
                            </div>
                          )}

                          {/* 환불 상태 표시 (환불 진행중인 경우, 사용자 취소는 제외) */}
                          {(() => {
                            const refund = refundMap[application.application_id];
                            // 환불 없거나, cancelled 상태인데 관리자 거절이 아닌 경우(사용자 취소) 표시 안함
                            if (!refund) return null;
                            if (refund.refund_status === 'cancelled' && !refund.rejection_reason) return null;
                            return (
                            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-semibold text-gray-800">환불 신청</h4>
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getRefundStatusColor(refund.refund_status)}`}>
                                    {getRefundStatusLabel(refund.refund_status)}
                                  </span>
                                </div>
                                {refund.refund_status === 'requested' && (
                                  <button
                                    onClick={() => handleCancelRefund(refund.id, application.application_id)}
                                    disabled={cancellingRefundId === refund.id}
                                    className="text-xs text-gray-500 hover:text-gray-700"
                                  >
                                    {cancellingRefundId === refund.id ? '취소 중...' : '신청 취소'}
                                  </button>
                                )}
                              </div>
                              {/* 환불 상태별 안내 메시지 */}
                              {refund.refund_status === 'requested' && (
                                <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded mb-3">
                                  관리자 승인 후 환불이 진행됩니다. 처리까지 3~4일 소요될 수 있습니다.
                                </p>
                              )}
                              {refund.refund_status === 'approved' && (
                                <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded mb-3">
                                  환불이 승인되었습니다. 입금 처리 중입니다.
                                </p>
                              )}
                              {refund.refund_status === 'completed' && (
                                <p className="text-xs text-green-700 bg-green-50 p-2 rounded mb-3">
                                  환불이 완료되었습니다.
                                </p>
                              )}
                              <div className="space-y-1 text-sm">
                                {refund.refund_amount != null && (
                                  <div><span className="text-gray-500">환불 금액</span> <span className="text-gray-900 font-medium">{refund.refund_amount.toLocaleString()}원</span></div>
                                )}
                                <div><span className="text-gray-500">환불 사유</span> <span className="text-gray-900">{refund.refund_reason || '-'}</span></div>
                                {refund.requested_at && (
                                  <div><span className="text-gray-500">신청일</span> <span className="text-gray-900">{new Date(refund.requested_at).toLocaleDateString('ko-KR')}</span></div>
                                )}
                                {refund.completed_at && (
                                  <div><span className="text-gray-500">완료일</span> <span className="text-gray-900">{new Date(refund.completed_at).toLocaleDateString('ko-KR')}</span></div>
                                )}
                                {refund.rejection_reason && (
                                  <div><span className="text-gray-500">거절 사유</span> <span className="text-red-600">{refund.rejection_reason}</span></div>
                                )}
                              </div>
                            </div>
                            );
                          })()}

                          {/* 버튼 영역 */}
                          {(application.status === 'registered' || application.status === 'waiting') && (
                            <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
                              {/* 영수증 보기 버튼 */}
                              {application.status === 'registered' && application.payment_status === 'completed' && application.receipt_url && (
                                <button
                                  onClick={() => {
                                    if (application.receipt_url) {
                                      window.open(application.receipt_url, '_blank');
                                    } else {
                                      alert('영수증 정보가 없습니다.');
                                    }
                                  }}
                                  className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                  영수증 보기
                                </button>
                              )}

                              {/* 환불 신청 버튼
                                  조건: registered + 결제완료 + (환불 없음 OR 환불 취소됨)
                                  - 결제 완료 상태에서만 환불 신청 가능
                                  - 환불 신청 중(requested) / 승인됨(approved) / 완료됨(completed) 상태면 불가
                                  - 환불 거부(cancelled with rejection_reason) 후에도 재신청 가능
                              */}
                              {application.status === 'registered' &&
                               application.payment_status === 'completed' &&
                               (!refundMap[application.application_id] ||
                                refundMap[application.application_id]?.refund_status === 'cancelled') && (
                                <button
                                  onClick={() => openRefundModal(application)}
                                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded transition-colors"
                                >
                                  환불 신청
                                </button>
                              )}

                              {/* 신청 취소 버튼
                                  조건: (결제 미완료 OR 무료수업) + (환불 없음 OR 환불 취소됨)
                                  - 무료 수업(payment_method='free')은 직접 취소 가능
                                  - 유료 수업 결제 완료 시 환불 신청으로 안내
                                  - 환불 신청 중(requested) / 승인됨(approved) / 완료됨(completed) 상태면 불가
                              */}
                              {(application.payment_status !== 'completed' || application.payment_method === 'free') &&
                               (!refundMap[application.application_id] ||
                                refundMap[application.application_id]?.refund_status === 'cancelled') && (
                                <button
                                  onClick={() => handleCancelApplication(application.application_id)}
                                  disabled={cancellingId === application.application_id}
                                  className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {cancellingId === application.application_id ? '취소 처리 중...' : '신청 취소'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div>
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 py-5 sm:p-6">
                {/* 저장 성공/실패 메시지 */}
                {saveMessage && (
                  <div className={`mb-6 p-4 rounded-lg ${saveMessage.includes('실패') ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <p className={`text-sm ${saveMessage.includes('실패') ? 'text-red-800' : 'text-green-800'}`}>
                      {saveMessage}
                    </p>
                  </div>
                )}

                {/* 본인인증 정보 (읽기 전용) */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">본인인증 정보</h3>
                    <span className="text-xs text-gray-500 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      변경 불가
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                      <p className="text-gray-900">{user.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">생년월일</label>
                      <p className="text-gray-900">{user.birthdate || '-'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">성별</label>
                      <p className="text-gray-900">
                        {user.gender === 'male' ? '남성' : user.gender === 'female' ? '여성' : '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">휴대폰 번호</label>
                      <p className="text-gray-900">{user.phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* 수정 가능한 정보 */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">회원 정보</h3>
                    {!editMode && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#48c9a6] hover:bg-[#3ab89a] rounded-lg transition-colors"
                      >
                        정보 수정
                      </button>
                    )}
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        이메일 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={!editMode}
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                          editMode
                            ? 'focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]'
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                      />
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        주소
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={!editMode}
                        placeholder="예: 경기도 동두천시"
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                          editMode
                            ? 'focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]'
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                      />
                    </div>

                    <div>
                      <label htmlFor="addressDetail" className="block text-sm font-medium text-gray-700 mb-1">
                        상세주소
                      </label>
                      <input
                        type="text"
                        id="addressDetail"
                        value={addressDetail}
                        onChange={(e) => setAddressDetail(e.target.value)}
                        disabled={!editMode}
                        placeholder="동/호수 등"
                        className={`block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm ${
                          editMode
                            ? 'focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]'
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                      />
                    </div>

                    {editMode && (
                      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          취소
                        </button>
                        <button
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="px-6 py-2 text-sm font-medium text-white bg-[#48c9a6] hover:bg-[#3ab89a] rounded-lg transition-colors disabled:opacity-50"
                        >
                          {saving ? '저장 중...' : '저장'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* 자녀 관리 섹션 제거 - 항상 본인 신청 구조 */}

                {/* 회원 탈퇴 */}
                <div className="mt-10 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleOpenDeleteModal}
                    className="text-sm text-gray-400 hover:text-red-500 transition-colors underline"
                  >
                    회원 탈퇴
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 자녀 모달 제거 - 항상 본인 신청 구조 */}
        </div>
      </div>

      {/* 환불 신청 모달 */}
      {refundModalOpen && refundTargetApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">환불 신청</h3>
              <button
                onClick={() => setRefundModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* 신청 정보 요약 */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{refundTargetApp.program_name}</p>
                {refundTargetApp.class_name && refundTargetApp.class_name !== '기본반' && (
                  <p className="text-sm text-gray-600">{refundTargetApp.class_name}</p>
                )}
                <p className="text-sm text-gray-600 mt-1">
                  환불 예정 금액: <span className="font-semibold text-gray-900">{refundTargetApp.payment_amount?.toLocaleString()}원</span>
                </p>
              </div>

              {/* 환불 사유 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  환불 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={3}
                  placeholder="환불 신청 사유를 입력해주세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                />
              </div>

              {/* 환불 계좌 정보 (선택) */}
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  환불 받으실 계좌 정보를 입력해주세요. (선택)
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">은행명</label>
                  <input
                    type="text"
                    value={refundBank}
                    onChange={(e) => setRefundBank(e.target.value)}
                    placeholder="예: 국민은행"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">계좌번호</label>
                  <input
                    type="text"
                    value={refundAccount}
                    onChange={(e) => setRefundAccount(e.target.value)}
                    placeholder="'-' 없이 숫자만 입력"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">예금주명</label>
                  <input
                    type="text"
                    value={refundAccountHolder}
                    onChange={(e) => setRefundAccountHolder(e.target.value)}
                    placeholder="예금주명"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                  />
                </div>
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  * 환불 신청 후 관리자 확인을 거쳐 처리됩니다.<br />
                  * 승인 전까지는 환불 신청을 취소할 수 있습니다.<br />
                  * 환불 완료 시 수강 신청이 자동으로 취소됩니다.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => setRefundModalOpen(false)}
                disabled={submittingRefund}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleSubmitRefund}
                disabled={submittingRefund || !refundReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#48c9a6] rounded-lg hover:bg-[#3ab89a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingRefund ? '신청 중...' : '환불 신청'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 회원 탈퇴 모달 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">회원 탈퇴</h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingAccount}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="text-sm text-gray-700 space-y-2">
                <p>탈퇴 시 계정 정보가 영구 삭제되며 복구할 수 없습니다.</p>
                {deleteWarning?.hasActiveApps && (
                  <p className="text-red-600 font-medium">
                    진행 중인 수강 신청이 모두 취소됩니다.
                  </p>
                )}
                {deleteWarning != null && deleteWarning.paidAmount > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-red-700 font-semibold">
                      환불받지 못한 수강료 {deleteWarning.paidAmount.toLocaleString()}원이 있습니다.
                    </p>
                    <p className="text-red-600 text-xs mt-1">
                      탈퇴 시 환불이 불가합니다.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호 확인
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="비밀번호를 입력해주세요"
                  disabled={deletingAccount}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !deletingAccount) handleDeleteAccount(); }}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingAccount}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || !deletePassword}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {deletingAccount ? '처리 중...' : '탈퇴하기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MyPage;